import { useState, useRef } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { CloudArrowUp, X, Plus } from '@phosphor-icons/react';
import { toast } from 'sonner';

const CATEGORIES = ['clubs', 'bags', 'balls', 'apparel', 'accessories', 'cart-tech'];
const CONDITIONS = ['new', 'like-new', 'good', 'fair', 'poor'];

export default function CreateListingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [form, setForm] = useState({ title: '', description: '', price: '', condition: 'good', category: 'clubs', location: '' });
  const [submitting, setSubmitting] = useState(false);

  if (!user) return <Navigate to="/auth" replace />;

  const handleFiles = (newFiles) => {
    const allowed = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    const valid = Array.from(newFiles).filter(f => allowed.includes(f.type) && f.size < 10 * 1024 * 1024);
    if (valid.length + files.length > 8) { toast.error('Max 8 photos'); return; }
    setFiles(prev => [...prev, ...valid]);
    setPreviews(prev => [...prev, ...valid.map(f => URL.createObjectURL(f))]);
  };

  const removeFile = (idx) => {
    setFiles(prev => prev.filter((_, i) => i !== idx));
    setPreviews(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error('Title required'); return; }
    if (!form.price || parseFloat(form.price) <= 0) { toast.error('Valid price required'); return; }
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('title', form.title);
      formData.append('description', form.description);
      formData.append('price', parseFloat(form.price));
      formData.append('condition', form.condition);
      formData.append('category', form.category);
      formData.append('location', form.location);
      files.forEach(f => formData.append('files', f));
      const { data } = await api.post('/listings', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Listing created!');
      navigate(`/shop/${data.listing_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create listing');
    } finally { setSubmitting(false); }
  };

  const update = (key) => (e) => setForm(prev => ({ ...prev, [key]: e.target.value }));

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-background" data-testid="create-listing-page">
      <div className="max-w-lg mx-auto px-6 py-10">
        <h1 className="font-serif text-3xl text-foreground font-medium tracking-tight" data-testid="create-listing-title">Sell an Item</h1>
        <p className="text-muted-foreground text-sm mt-2 font-sans">List your golf equipment for sale</p>

        <form onSubmit={handleSubmit} className="mt-8 space-y-6">
          {/* Photos */}
          <div className="space-y-2">
            <Label className="text-xs tracking-wide uppercase text-muted-foreground font-sans font-semibold">Photos (up to 8)</Label>
            <div className="flex gap-2 flex-wrap">
              {previews.map((p, i) => (
                <div key={i} className="relative w-20 h-20 border border-border rounded-sm overflow-hidden">
                  <img src={p} alt="" className="w-full h-full object-cover" />
                  <button type="button" onClick={() => removeFile(i)} className="absolute top-0.5 right-0.5 w-5 h-5 bg-black/60 rounded-full flex items-center justify-center">
                    <X size={10} className="text-white" />
                  </button>
                </div>
              ))}
              {files.length < 8 && (
                <button type="button" onClick={() => fileRef.current?.click()}
                  className="w-20 h-20 border-2 border-dashed border-border rounded-sm flex items-center justify-center hover:border-primary/50 transition-colors"
                  data-testid="add-photo-btn"
                >
                  <Plus size={20} className="text-muted-foreground" />
                </button>
              )}
            </div>
            <input ref={fileRef} type="file" accept="image/*" multiple onChange={(e) => handleFiles(e.target.files)} className="hidden" data-testid="listing-file-input" />
          </div>

          {/* Title */}
          <div className="space-y-2">
            <Label className="text-xs tracking-wide uppercase text-muted-foreground font-sans font-semibold">Title</Label>
            <input value={form.title} onChange={update('title')} placeholder="e.g., TaylorMade Stealth 2 Driver"
              className="w-full bg-transparent border-0 border-b border-border focus:border-primary focus:outline-none focus:ring-0 px-0 py-2 text-sm font-sans" required data-testid="listing-title-input" />
          </div>

          {/* Price */}
          <div className="space-y-2">
            <Label className="text-xs tracking-wide uppercase text-muted-foreground font-sans font-semibold">Price ($)</Label>
            <input type="number" min="0" step="0.01" value={form.price} onChange={update('price')} placeholder="0.00"
              className="w-full bg-transparent border-0 border-b border-border focus:border-primary focus:outline-none focus:ring-0 px-0 py-2 text-sm font-sans" required data-testid="listing-price-input" />
          </div>

          {/* Category + Condition */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-xs tracking-wide uppercase text-muted-foreground font-sans font-semibold">Category</Label>
              <select value={form.category} onChange={update('category')}
                className="w-full bg-transparent border-0 border-b border-border focus:border-primary focus:outline-none text-sm font-sans py-2 capitalize" data-testid="listing-category-select">
                {CATEGORIES.map(c => <option key={c} value={c} className="capitalize">{c}</option>)}
              </select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs tracking-wide uppercase text-muted-foreground font-sans font-semibold">Condition</Label>
              <select value={form.condition} onChange={update('condition')}
                className="w-full bg-transparent border-0 border-b border-border focus:border-primary focus:outline-none text-sm font-sans py-2 capitalize" data-testid="listing-condition-select">
                {CONDITIONS.map(c => <option key={c} value={c} className="capitalize">{c}</option>)}
              </select>
            </div>
          </div>

          {/* Location */}
          <div className="space-y-2">
            <Label className="text-xs tracking-wide uppercase text-muted-foreground font-sans font-semibold">Location</Label>
            <input value={form.location} onChange={update('location')} placeholder="City, State"
              className="w-full bg-transparent border-0 border-b border-border focus:border-primary focus:outline-none focus:ring-0 px-0 py-2 text-sm font-sans" data-testid="listing-location-input" />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label className="text-xs tracking-wide uppercase text-muted-foreground font-sans font-semibold">Description</Label>
            <textarea value={form.description} onChange={update('description')} placeholder="Describe your item..."
              rows={4} maxLength={2000}
              className="w-full bg-transparent border-0 border-b border-border focus:border-primary focus:outline-none focus:ring-0 resize-none px-0 py-2 text-sm font-sans" data-testid="listing-description-input" />
          </div>

          <Button type="submit" disabled={submitting}
            className="w-full h-12 rounded-sm bg-primary hover:bg-[#083313] transition-colors duration-300 text-sm tracking-wide uppercase font-semibold font-sans"
            data-testid="listing-submit-btn">
            {submitting ? 'Creating...' : 'Publish Listing'}
          </Button>
        </form>
      </div>
    </div>
  );
}
